import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

type ContactType = 'general' | 'commercial' | 'partnership';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [contactType, setContactType] = useState<ContactType>('general');
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
    segment: "",
    service: "",
    partnershipType: ""
  });
  const [submitted, setSubmitted] = useState(false);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, this would send the form data to a server
    console.log("Form submitted:", formData);
    setSubmitted(true);
  };
  
  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-mypa-blue mb-4">{t("contact.title")}</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t("contact.description")}</p>
      </div>
      
      {submitted ? (
        <div className="max-w-2xl mx-auto bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <h2 className="text-2xl font-bold text-mypa-blue mb-2">{t("contact.success.title")}</h2>
          <p className="text-gray-600 mb-6">{t("contact.success.message")}</p>
          <button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: "",
                email: "",
                company: "",
                phone: "",
                message: "",
                segment: "",
                service: "",
                partnershipType: ""
              });
            }}
            className="bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
          >
            {t("contact.success.button")}
          </button>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="md:flex">
              {/* Contact form */}
              <div className="md:w-2/3 p-8">
                {/* Contact type selector */}
                <div className="mb-8">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setContactType('general')}
                      className={`px-4 py-2 rounded-full transition-colors ${
                        contactType === 'general'
                          ? "bg-mypa-green text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {t("contact.types.general")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setContactType('commercial')}
                      className={`px-4 py-2 rounded-full transition-colors ${
                        contactType === 'commercial'
                          ? "bg-mypa-green text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {t("contact.types.commercial")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setContactType('partnership')}
                      className={`px-4 py-2 rounded-full transition-colors ${
                        contactType === 'partnership'
                          ? "bg-mypa-green text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {t("contact.types.partnership")}
                    </button>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit}>
                  {/* Common fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-gray-700 mb-2">{t("contact.form.name")}</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-700 mb-2">{t("contact.form.email")}</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="company" className="block text-gray-700 mb-2">{t("contact.form.company")}</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-gray-700 mb-2">{t("contact.form.phone")}</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                      />
                    </div>
                  </div>
                  
                  {/* Specific fields based on contact type */}
                  {contactType === 'commercial' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label htmlFor="segment" className="block text-gray-700 mb-2">{t("contact.form.segment")}</label>
                        <select
                          id="segment"
                          name="segment"
                          value={formData.segment}
                          onChange={handleInputChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                        >
                          <option value="">{t("contact.form.select")}</option>
                          <option value="cbam">{t("contact.form.segments.cbam")}</option>
                          <option value="large">{t("contact.form.segments.large")}</option>
                          <option value="sme">{t("contact.form.segments.sme")}</option>
                          <option value="small">{t("contact.form.segments.small")}</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="service" className="block text-gray-700 mb-2">{t("contact.form.service")}</label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                        >
                          <option value="">{t("contact.form.select")}</option>
                          <option value="discovery">{t("contact.form.services.discovery")}</option>
                          <option value="performance">{t("contact.form.services.performance")}</option>
                          <option value="strategy">{t("contact.form.services.strategy")}</option>
                        </select>
                      </div>
                    </div>
                  )}
                  
                  {contactType === 'partnership' && (
                    <div className="mb-6">
                      <label htmlFor="partnershipType" className="block text-gray-700 mb-2">{t("contact.form.partnership_type")}</label>
                      <select
                        id="partnershipType"
                        name="partnershipType"
                        value={formData.partnershipType}
                        onChange={handleInputChange}
                        required
                        className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                      >
                        <option value="">{t("contact.form.select")}</option>
                        <option value="technology">{t("contact.form.partnership_types.technology")}</option>
                        <option value="distribution">{t("contact.form.partnership_types.distribution")}</option>
                        <option value="content">{t("contact.form.partnership_types.content")}</option>
                        <option value="research">{t("contact.form.partnership_types.research")}</option>
                      </select>
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-gray-700 mb-2">{t("contact.form.message")}</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-mypa-green"
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                    >
                      {t("contact.form.submit")}
                    </button>
                  </div>
                </form>
              </div>
              
              {/* Contact information */}
              <div className="md:w-1/3 bg-mypa-blue text-white p-8">
                <h3 className="text-2xl font-bold mb-6">{t("contact.info.title")}</h3>
                
                <div className="mb-8">
                  <h4 className="font-bold mb-2">{t("contact.info.address.title")}</h4>
                  <p className="mb-1">{t("contact.info.address.line1")}</p>
                  <p>{t("contact.info.address.line2")}</p>
                </div>
                
                <div className="mb-8">
                  <h4 className="font-bold mb-2">{t("contact.info.email.title")}</h4>
                  <p className="mb-1">{t("contact.info.email.general")}</p>
                  <p>{t("contact.info.email.support")}</p>
                </div>
                
                <div className="mb-8">
                  <h4 className="font-bold mb-2">{t("contact.info.phone.title")}</h4>
                  <p>{t("contact.info.phone.number")}</p>
                </div>
                
                <div>
                  <h4 className="font-bold mb-2">{t("contact.info.social.title")}</h4>
                  <div className="flex space-x-4">
                    <a href="#" className="text-white hover:text-gray-200">
                      <span className="sr-only">LinkedIn</span>
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                      </svg>
                    </a>
                    <a href="#" className="text-white hover:text-gray-200">
                      <span className="sr-only">Twitter</span>
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* FAQ Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-mypa-blue mb-8 text-center">{t("contact.faq.title")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("contact.faq.q1.question")}</h3>
                <p className="text-gray-600">{t("contact.faq.q1.answer")}</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("contact.faq.q2.question")}</h3>
                <p className="text-gray-600">{t("contact.faq.q2.answer")}</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("contact.faq.q3.question")}</h3>
                <p className="text-gray-600">{t("contact.faq.q3.answer")}</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("contact.faq.q4.question")}</h3>
                <p className="text-gray-600">{t("contact.faq.q4.answer")}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
